import { forwardRef, useContext, type CSSProperties, type ReactNode } from 'react'
import { motion, type Variants } from 'framer-motion'
import { ICON_STEP, type IconSize } from '../tokens/typography'
import { cn } from '../lib/cn'
import { IconStateContext, type IconState } from './state'

/** A mark sits about a tenth over its text. */
const TEXT_RELATIVE = '1.1em'

export interface IconProps {
  /** Defaults to `text`: sized from the text the mark sits in. */
  size?: IconSize
  /** Overrides the state inherited from the surrounding control. */
  state?: IconState
  className?: string
  style?: CSSProperties
}

/**
 * What a mark must accept to live in the registry. Lucide icons satisfy this
 * as they are; the drawn marks in `drawn.tsx` are written to it directly.
 */
export type MarkComponent = (props: {
  size?: string | number
  strokeWidth?: number
  className?: string
  style?: CSSProperties
}) => ReactNode

export function IconStateProvider({ state, children }: { state: IconState; children: ReactNode }) {
  return <IconStateContext.Provider value={state}>{children}</IconStateContext.Provider>
}

/** Every state a glyph did not declare falls back to `rest`, so poses never accumulate. */
function complete(v: Variants | undefined): Variants | undefined {
  if (!v) return undefined
  const rest = v.rest ?? {}
  return { rest, hover: v.hover ?? rest, press: v.press ?? v.hover ?? rest, on: v.on ?? rest, live: v.live ?? rest }
}

/**
 * Binds a Lucide mark to a concept. The stroke is held at 1.2 device pixels
 * at every size by `non-scaling-stroke` (see `.wf-icon`).
 */
export function fromLucide(Mark: MarkComponent, name: string, variants?: Variants) {
  const resolved = complete(variants)

  const Concept = forwardRef<HTMLSpanElement, IconProps>(function Concept({ size = 'text', state, className, style }, ref) {
    const inherited = useContext(IconStateContext)
    const box = size === 'text' ? TEXT_RELATIVE : ICON_STEP[size]
    return (
      <motion.span
        ref={ref}
        aria-hidden
        initial={resolved ? 'rest' : undefined}
        animate={resolved ? (state ?? inherited) : undefined}
        variants={resolved}
        className={cn('wf-icon inline-flex shrink-0 items-center justify-center', className)}
        style={{ width: box, height: box, ...style }}
      >
        <Mark size="100%" strokeWidth={1.2} />
      </motion.span>
    )
  })

  Concept.displayName = name
  return Concept
}
